import {
  Box,
  ButtonBase,
  Icon,
  styled,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip
} from '@mui/material';
import useSettings from 'app/hooks/useSettings';
import { NavLink } from 'react-router-dom';
import { Paragraph, Span } from '../Typography';
import AppVerticalNavExpansionPannel from './AppVerticalNavExpansionPannel';
import React, { Fragment } from 'react';
import useAuth from 'app/hooks/useAuth';
import { typography } from 'app/utils/constant';
import sidebarImg from '../../../assets/SidebarImage.png';

const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: '12px',
  marginTop: '5px',
  marginLeft: '15px',
  marginBottom: '5px',
  textTransform: 'uppercase',
  display: mode === 'compact' && 'none',
  color: theme.palette.text.secondary,
}));

const ExtAndIntCommon = {
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '4px',
  height: 40,
  whiteSpace: 'pre',
  marginBottom: '8px',
  textDecoration: 'none',
  justifyContent: 'space-between',
  alignItems: 'center',
  transition: 'all 150ms ease-in',
  '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
  '&.compactNavItem': {
    overflow: 'hidden',
    justifyContent: 'center !important',
  },
};

const ExternalLink = styled('a')(({ theme }) => ({
  ...ExtAndIntCommon,
  color: theme.palette.text.primary,
}));

const InternalLink = styled(Box)(({ theme }) => ({
  '& a': {
    ...ExtAndIntCommon,
    color: theme.palette.text.primary,
  },
  '& .navItemActive': {
    backgroundColor: 'rgba(22, 77, 80, 0.16)',
  },
}));

const StyledText = styled(Span)(({ theme, mode }) => ({
  ...typography.buttonSecondary,
  color: theme.palette.secondary.contrastText,
  display: mode === 'compact' && 'none',
}));

const BulletIcon = styled('div')(({ theme }) => ({
  padding: '2px',
  marginLeft: '24px',
  marginRight: '8px',
  overflow: 'hidden',
  borderRadius: '300px',
  background: theme.palette.text.primary,
}));

const BadgeValue = styled('div')(() => ({
  padding: '1px 8px',
  overflow: 'hidden',
  borderRadius: '300px',
}));

const AppVerticalNav = ({ items }) => {
  const { settings, updateSettings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const theme = useTheme();
  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = mode === 'compact';

  const { user } = useAuth();

  const updateSidebarMode = (sidebarSettings) => {
    updateSettings({
      layout1Settings: { leftSidebar: { ...sidebarSettings } },
    });
  };

  const handleSidebarToggle = () => {
    let { layout1Settings } = settings;
    let newMode;
    if (isMdScreen) {
      newMode =
        layout1Settings.leftSidebar.mode === 'mobile' ? 'close' : 'mobile';
    } else {
      newMode =
        layout1Settings.leftSidebar.mode === 'full' ? 'close' : 'full';
    }
    updateSidebarMode({ mode: newMode });
  };

  const renderLevels = (data, ifChild) => {
    return data.map((item, index) => {
      // Divider position before settings section
      if (index === 4) {
        return (
          <Fragment key={`divider-${index}`}>
            <Divider sx={{ my: 1.5, borderColor: 'divider' }} />

            {renderMenuItem(item, index, ifChild)}

          
          </Fragment>
        );
      }

      return renderMenuItem(item, index, ifChild);
    });
  };

  // FULL MENU RENDER LOGIC WITH ROLE CHECK
  const renderMenuItem = (item, index, ifChild) => {

    // LABEL
    if (item.type === 'label') {
      return (
        <ListLabel key={index} className="sidenavHoverShow">
          {item.label}
        </ListLabel>
      );
    }

    // PARENT MENU — add role check
    if (item.children) {
      if (item.role?.includes(user.Type)) {
        return (
          <AppVerticalNavExpansionPannel mode={mode} item={item} key={index}>
            {renderLevels(item.children, true)}
          </AppVerticalNavExpansionPannel>
        );
      }
      return null;
    }

    // EXTERNAL LINK
    if (item.type === 'extLink') {
      return (
        <ExternalLink key={index} href={item.path} target="_blank">
          <ButtonBase sx={{ width: '100%' }}>
            {item.icon ? (
              <Icon className="icon">{item.icon}</Icon>
            ) : (
              <span className="item-icon icon-text">{item.iconText}</span>
            )}
            <StyledText className="sidenavHoverShow">{item.name}</StyledText>
          </ButtonBase>
        </ExternalLink>
      );
    }

    // NORMAL MENU ITEMS — role check
    if (item.role && !item.role.includes(user.Type)) {
      return null;
    }

    // DEFAULT INTERNAL MENU ITEM
    return (
      <InternalLink key={index}>
        <NavLink
          to={item.path}
          state={{ accessID: item.accessID }}
          className={({ isActive }) => (isActive ? 'navItemActive' : '')}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Tooltip
            title={isCompact ? item.name : ''}
            placement="right"
            arrow
            enterDelay={300}
            disableInteractive
          >
            <ButtonBase
              sx={{
                width: '100%',
                paddingLeft: ifChild ? '10px' : '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                transition: '0.2s',
                '&:hover': {
                  bgcolor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              {item.icon && (
                <Icon
                  sx={{
                    fontSize: 24,
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {item.icon}
                </Icon>
              )}

              {!isCompact && (
                <Box
                  sx={{
                    ml: 1,
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                >
                  {item.name}
                </Box>
              )}

              <Box flexGrow={1} />

              {item.badge && (
                <BadgeValue className="sidenavHoverShow">
                  {item.badge.value}
                </BadgeValue>
              )}
            </ButtonBase>
          </Tooltip>
        </NavLink>
      </InternalLink>
    );
  };

  return <div className="navigation">{renderLevels(items, false)}</div>;
};

export default React.memo(AppVerticalNav);


// import {
//   Box,
//   ButtonBase,
//   Icon,
//   styled,
//   useMediaQuery,
//   useTheme,  Divider,
// Typography,
// Tooltip
// } from '@mui/material';
// import useSettings from 'app/hooks/useSettings';
// import { NavLink } from 'react-router-dom';
// import { Paragraph, Span } from '../Typography';
// import AppVerticalNavExpansionPannel from './AppVerticalNavExpansionPannel';
// import React, { Fragment } from 'react';
// import useAuth from 'app/hooks/useAuth';
// import { typography } from 'app/utils/constant';
// import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
// import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
// import sidebarImg from '../../../assets/SidebarImage.png'
// const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
//   fontSize: '12px',
//   marginTop: '5px',
//   marginLeft: '15px',
//   marginBottom: '5px',
//   textTransform: 'uppercase',
//   display: mode === 'compact' && 'none',
//   color: theme.palette.text.secondary,
// }));
// const ExtAndIntCommon = {
//   display: 'flex',
//   overflow: 'hidden',
//   borderRadius: '4px',
//   height: 40, // give enough height for icon + text
//   whiteSpace: 'pre',
//   marginBottom: '8px',
//   textDecoration: 'none',
//   justifyContent: 'space-between',
//   alignItems: 'center',
//   transition: 'all 150ms ease-in',
//   '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
//   '&.compactNavItem': {
//     overflow: 'hidden',
//     justifyContent: 'center !important',
//   },
// };

// // const ExtAndIntCommon = {
// //   display: 'flex',
// //   overflow: 'hidden',
// //   borderRadius: '4px',
// //   height: 27,
// //   whiteSpace: 'pre',
// //   marginBottom: '8px',
// //   textDecoration: 'none',
// //   justifyContent: 'space-between',
// //   transition: 'all 150ms ease-in',
// //   '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
// //   '&.compactNavItem': {
// //     overflow: 'hidden',
// //     justifyContent: 'center !important',
// //   },
// //   '& .icon': {
// //     fontSize: '18px',
// //     paddingLeft: '16px',
// //     paddingRight: '16px',
// //     verticalAlign: 'middle',
// //   },
// // };

// const ExternalLink = styled('a')(({ theme }) => ({
//   ...ExtAndIntCommon,
//   color: theme.palette.text.primary,
// }));

// const InternalLink = styled(Box)(({ theme }) => ({
//   '& a': {
//     ...ExtAndIntCommon,
//     color: theme.palette.text.primary,
//   },
//   '& .navItemActive': {
//     backgroundColor: 'rgba(22, 77, 80, 0.16)',
//   },
// }));

// const StyledText = styled(Span)(({theme, mode }) => ({
//     ...typography.buttonSecondary,
//          color: theme.palette.secondary.contrastText,
//   // fontSize: '0.875rem',
//   // paddingLeft: '0.8rem',
//   display: mode === 'compact' && 'none',
// }));

// const BulletIcon = styled('div')(({ theme }) => ({
//   padding: '2px',
//   marginLeft: '24px',
//   marginRight: '8px',
//   overflow: 'hidden',
//   borderRadius: '300px',
//   background: theme.palette.text.primary,
// }));

// const BadgeValue = styled('div')(() => ({
//   padding: '1px 8px',
//   overflow: 'hidden',
//   borderRadius: '300px',
// }));

// const AppVerticalNav = ({ items }) => {
//   const { settings, updateSettings } = useSettings();
//   const { mode } = settings.layout1Settings.leftSidebar;
//   const theme = useTheme();
//   const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
// const isCompact = mode === "compact";
//   const updateSidebarMode = (sidebarSettings) => {
//     updateSettings({
//       layout1Settings: { leftSidebar: { ...sidebarSettings } },
//     });
//   };
// const { user } = useAuth();
//   const handleSidebarToggle = () => {
//     let { layout1Settings } = settings;
//     let newMode;
//     if (isMdScreen) {
//       newMode =
//         layout1Settings.leftSidebar.mode === 'mobile' ? 'close' : 'mobile';
//     } else {
//       newMode =
//         layout1Settings.leftSidebar.mode === 'full' ? 'close' : 'full';
//     }
//     updateSidebarMode({ mode: newMode });
//   };

//  const renderLevels = (data, ifChild) => {
//   return data.map((item, index) => {
//     // --- Divider after the 4th menu item ---
//     if (index === 7) {
//       return (
//         <Fragment key={`divider-${index}`}>
//           <Divider sx={{ my: 1.5, borderColor: "divider" }} />
//           {/* Continue rendering the 4th item after divider */}
//           {renderMenuItem(item, index, ifChild)}
//           {/* Add Settings and Support after divider */}
//           <Box mt={1}>
          

//             {/* Image at bottom */}
//             <Box
//               sx={{
//                 mt: 2,
//                 p: 1.5,
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//               }}
//             >
//               <Box
//                 component="img"
//                 src={sidebarImg}
//                 alt="Sidebar Footer"
//                 sx={{
//                   width: "120px",
//                   borderRadius: 2,
//                   opacity: 0.85,
//                 }}
//               />
//             </Box>
//           </Box>
//         </Fragment>
//       );
//     }

//     // --- Default render for all other items ---
//     return renderMenuItem(item, index, ifChild);
//   });
// };

// // --- Helper function to keep the main item rendering clean ---
// const renderMenuItem = (item, index, ifChild) => {
//   if (item.type === "label") {
//     return (
//       <ListLabel key={index} className="sidenavHoverShow">
//         {item.label}
//       </ListLabel>
//     );
//   }

//   if (item.children) {
//      if (item.role.includes(user.Type)) {
//           return (
//             <AppVerticalNavExpansionPannel mode={mode} item={item} key={index}>
//               {renderLevels(item.children, true)}
//             </AppVerticalNavExpansionPannel>
//           );
//         }
//     // return (

//     //   <AppVerticalNavExpansionPannel item={item} key={index}>
//     //     {renderLevels(item.children, true)}
//     //   </AppVerticalNavExpansionPannel>
//     // );
//   }

//   if (item.type === "extLink") {
//     return (
//       <ExternalLink key={index} href={item.path} target="_blank">
//         <ButtonBase sx={{ width: "100%" }}>
//           {item.icon ? (
//             <Icon className="icon">{item.icon}</Icon>
//           ) : (
//             <span className="item-icon icon-text">{item.iconText}</span>
//           )}
//           <StyledText className="sidenavHoverShow">{item.name}</StyledText>
//         </ButtonBase>
//       </ExternalLink>
//     );
//   }
// return (
//   <InternalLink key={index}>
//     <NavLink
//       to={item.path}
//       state={{ accessID: item.accessID }}
//       className={({ isActive }) => (isActive ? "navItemActive" : "")}
//       style={{ textDecoration: "none", color: "inherit" }}
//     >
//       <Tooltip
//         title={isCompact ? item.name : ""}
//         placement="right"
//         arrow
//         enterDelay={300}
//         disableInteractive
//       >
//         <ButtonBase
//           sx={{
//             width: "100%",
//             paddingLeft: ifChild ? "10px" : "0px",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "flex-start",
//             gap: 1.5,
//             px: 1.5,
//             py: 1,
//             borderRadius: 2,
//             transition: "0.2s",
//             "&:hover": {
//               bgcolor: (theme) => theme.palette.action.hover,
//             },
//           }}
//         >
//           {item.icon && (
//             <Icon
//               sx={{
//                 fontSize: 24,
//                 color: "text.secondary",
//                 display: "flex",
//                 alignItems: "center",
//               }}
//             >
//               {item.icon}
//             </Icon>
//           )}

//           {/* ✅ Only show text when not compact */}
//           {!isCompact && (
//             <Box
//               sx={{
//                 ml: 1,
//                 fontSize: 14,
//                 fontWeight: 500,
//                 color: "text.primary",
//               }}
//             >
//               {item.name}
//             </Box>
//           )}

//           <Box flexGrow={1} />

//           {item.badge && (
//             <BadgeValue className="sidenavHoverShow">
//               {item.badge.value}
//             </BadgeValue>
//           )}
//         </ButtonBase>
//       </Tooltip>
//     </NavLink>
//   </InternalLink>
// );
// };

//   return <div className="navigation">{renderLevels(items, false)}</div>;
// };

// export default React.memo(AppVerticalNav);
  {/* <InternalLink>
              <NavLink to="/setting">
                <ButtonBase
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <SettingsOutlinedIcon
                    sx={{ fontSize: 22, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.primary">
                    Settings
                  </Typography>
                </ButtonBase>
              </NavLink>
            </InternalLink> */}

            {/* <InternalLink>
              <NavLink to="/support">
                <ButtonBase
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <SupportAgentOutlinedIcon
                    sx={{ fontSize: 22, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.primary">
                    Support
                  </Typography>
                </ButtonBase>
              </NavLink>
            </InternalLink> */}
 {/* <ButtonBase
  onClick={() => {
    if (isMdScreen) handleSidebarToggle(); // Only close on mobile view
  }}
  sx={{ width: '100%', paddingLeft: ifChild ? '10px' : '0px' }}
>
              {item.icon ? (
                <Icon className="icon" sx={{ width: 36,color: theme.palette.icon }}>
                  {item.icon}
                </Icon>
              ) : (
                <Fragment>
                  <BulletIcon
                    className="nav-bullet"
                    sx={{ display: mode === 'compact' && 'none' }}
                  />
                  <Box
                    className="nav-bullet-text"
                    sx={{
                      ml: '10px',
                      fontSize: '11px',
                      display: mode !== 'compact' && 'none',
                    }}
                  >
                    {item.iconText}
                  </Box>
                </Fragment>
              )}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto" />
              {item.badge && (
                <BadgeValue className="sidenavHoverShow">
                  {item.badge.value}
                </BadgeValue>
              )}
            </ButtonBase> */}


// import { Box, ButtonBase, Icon, styled, useMediaQuery, useTheme }  from '@mui/material'
// import useSettings from 'app/hooks/useSettings'
// import { NavLink } from 'react-router-dom'
// import { Paragraph,Span } from '../Typography'
// import AppVerticalNavExpansionPannel from './AppVerticalNavExpansionPannel'
// import React,{ Fragment} from 'react'
// import useAuth from 'app/hooks/useAuth'




// const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
//   fontSize: '12px',
//   marginTop: '5px',
//   marginLeft: '15px',
//   marginBottom: '5px',
//   textTransform: 'uppercase',
//   display: mode === 'compact' && 'none',
//   color: theme.palette.text.secondary,
// }));

// const ExtAndIntCommon = {
//   display: 'flex',
//   overflow: 'hidden',
//   borderRadius: '4px',
//   height: 27,
//   whiteSpace: 'pre',
//   marginBottom: '8px',
//   textDecoration: 'none',
//   justifyContent: 'space-between',
//   transition: 'all 150ms ease-in',
//   '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
//   '&.compactNavItem': {
//     overflow: 'hidden',
//     justifyContent: 'center !important',
//   },
//   '& .icon': {
//     fontSize: '18px',
//     paddingLeft: '16px',
//     paddingRight: '16px',
//     verticalAlign: 'middle',
//   },
// };
// const ExternalLink = styled('a')(({ theme }) => ({
//   ...ExtAndIntCommon,
//   color: theme.palette.text.primary,
// }));

// const InternalLink = styled(Box)(({ theme }) => ({
//   '& a': {
//     ...ExtAndIntCommon,
//     color: theme.palette.text.primary,
//   },
//   '& .navItemActive': {
//     backgroundColor: 'rgba(22, 77, 80, 0.16)',
//   },
// }));

// const StyledText = styled(Span)(({ mode }) => ({
//   fontSize: '0.875rem',
//   paddingLeft: '0.8rem',
//   display: mode === 'compact' && 'none',
// }));

// const BulletIcon = styled('div')(({ theme }) => ({
//   padding: '2px',
//   marginLeft: '24px',
//   marginRight: '8px',
//   overflow: 'hidden',
//   borderRadius: '300px',
//   background: theme.palette.text.primary,
// }));

// const BadgeValue = styled('div')(() => ({
//   padding: '1px 8px',
//   overflow: 'hidden',
//   borderRadius: '300px',
// }));

// const AppVertivalNav = ({ items }) => {
//   const { settings,updateSettings } = useSettings();
//   const { user } = useAuth()
//   const { mode } = settings.layout1Settings.leftSidebar;
//   const theme = useTheme();
//   const isMdScreen = useMediaQuery(theme.breakpoints.down("md"));

//   const updateSidebarMode = (sidebarSettings) => {
//     updateSettings({
//       layout1Settings: { leftSidebar: { ...sidebarSettings } },
//     });
//   };
//   const handleSidebarToggle = () => {
//     let { layout1Settings } = settings;
//     let mode;
//     if (isMdScreen) {
//       mode = layout1Settings.leftSidebar.mode === "mobile" ? "close" : "mobile";
//     } else {
//       mode = layout1Settings.leftSidebar.mode === "full" ? "full" : "close";
//     }
//     updateSidebarMode({ mode });
//   };

//   const renderLevels = (data,ifChild) => {
//     return data.map((item, index) => {
//       if (item.type === 'label')
//         return (
//           <ListLabel key={index} mode={mode} className="sidenavHoverShow">
//             {item.label}
//           </ListLabel>
//         );

//       if (item.children) {
//         // console.log("🚀 ~ file: AppVertivalNav.jsx:93 ~ returndata.map ~ item.children:", item.children)
        
//         return (
//           <AppVerticalNavExpansionPannel mode={mode} item={item} key={index}>
//             {renderLevels(item.children,true)}
//           </AppVerticalNavExpansionPannel>
//         );
//       } else if (item.type === 'extLink') {
//         return (
//           <ExternalLink
//             key={index}
//             href={item.path}
//             className={`${mode === 'compact' && 'compactNavItem'}`}
//             rel="noopener noreferrer"
//             target="_blank"
//           >
//             <ButtonBase key={item.name} name="child" sx={{ width: '100%' }}>
//               {(() => {
//                 if (item.icon) {
//                   return <Icon className="icon">{item.icon}</Icon>;
//                 } else {
//                   return <span className="item-icon icon-text">{item.iconText}</span>;
//                 }
//               })()}
//               <StyledText mode={mode} className="sidenavHoverShow">
//                 {item.name}
//               </StyledText>
//               <Box mx="auto"></Box>
//               {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
//             </ButtonBase>
//           </ExternalLink>
//         );
//       } 
//       // else { 
//       //   if(item.role.includes(user.role)){
//       //   return (
//       //     <InternalLink key={index}>
//       //       <NavLink
//       //         to={item.path}
//       //         state={{accessID:item.accessID}}
//       //         className={({ isActive }) =>
//       //           isActive
//       //             ? `navItemActive ${mode === 'compact' && 'compactNavItem'}`
//       //             : `${mode === 'compact' && 'compactNavItem'}`
//       //         }
//       //       >
//       //         <ButtonBase onClick={handleSidebarToggle}  key={item.name} name="child" sx={{ width: '100%',paddingLeft: ifChild ?"10px" : "0px"}}>
//       //           {item?.icon ? (
//       //             <Icon className="icon" sx={{ width: 36 }}>
//       //               {item.icon}
//       //             </Icon>
//       //           ) : (
//       //             <Fragment>
//       //               <BulletIcon
//       //                 className={`nav-bullet`}
//       //                 sx={{ display: mode === 'compact' && 'none' }}
//       //               />
//       //               <Box
//       //                 className="nav-bullet-text"
//       //                 sx={{
//       //                   ml: '10px',
//       //                   fontSize: '11px',
//       //                   display: mode !== 'compact' && 'none',
//       //                 }}
//       //               >
//       //                 {item.iconText}
//       //               </Box>
//       //             </Fragment>
//       //           )}
//       //           <StyledText mode={mode} className="sidenavHoverShow">
//       //             {item.name}
//       //           </StyledText>

//       //           <Box mx="auto" />

//       //           {item.badge && (
//       //             <BadgeValue className="sidenavHoverShow">{item.badge.value}</BadgeValue>
//       //           )}
//       //         </ButtonBase>
//       //       </NavLink>
//       //     </InternalLink>
//       //   );}
//       // }
//     });
//   };

//   return <div className="navigation">{renderLevels(items,false)}</div>;
// };
  
//   export default React.memo(AppVertivalNav);